/**
 * Authentication Service
 * Handles magic link authentication flow
 */

import { apiClient } from './api';
import type { MagicLinkRequest, AuthToken, Applicant } from '../types';
import { ensureAuthToken, ensureApplicant } from '@/lib/api/type-guards';

export const authService = {
  /**
   * Request a magic link to be sent to the provided email
   */
  async requestMagicLink(email: string, redirectUrl?: string): Promise<void> {
    const payload: MagicLinkRequest = {
      email,
      redirect_url: redirectUrl,
    };

    await apiClient.post('/auth/magic-link/request', payload);
  },

  /**
   * Validate magic link token and get authentication token
   */
  async validateMagicLink(token: string): Promise<AuthToken> {
    const data = await apiClient.post<AuthToken>(
      '/auth/magic-link/validate',
      { token }
    );

    // Validate response
    const response = ensureAuthToken(data);

    // Store token in localStorage (using applicant_auth_token for consistency)
    localStorage.setItem('applicant_auth_token', response.token);
    localStorage.setItem('applicant_auth_expires_at', response.expires_at);

    return response;
  },

  /**
   * Get current user profile
   */
  async getCurrentUser(): Promise<Applicant> {
    const data = await apiClient.get<Applicant>('/auth/me');
    return ensureApplicant(data);
  },

  /**
   * Logout user (clear local storage)
   */
  logout(): void {
    localStorage.removeItem('applicant_auth_token');
    localStorage.removeItem('applicant_auth_expires_at');
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem('applicant_auth_token');
    const expiresAt = localStorage.getItem('applicant_auth_expires_at');

    if (!token || !expiresAt) {
      return false;
    }

    // Check if token is expired
    const expiryDate = new Date(expiresAt);
    const now = new Date();

    if (now >= expiryDate) {
      this.logout();
      return false;
    }

    return true;
  },

  /**
   * Get stored token
   */
  getToken(): string | null {
    return localStorage.getItem('applicant_auth_token');
  },
};